import { z } from 'zod';
import { AnswerShapeSchema } from './clarification';
import { ControlImplementationSchema } from './control-catalogue';
import { ProseOriginSchema, SEVERITIES, SeveritySchema } from './enums';
import { QrsControlIdSchema, ThreatPatternIdSchema } from './ids';
import { NonEmptyStringSchema, TimestampSchema } from './primitives';
import { SlotKindSchema } from './statement';

/**
 * What a **document** made of findings and questions is
 * (docs/DECISIONS.md ZD-057).
 *
 * `FindingStatement` decided what one finding says and `ClarificationStatement`
 * what one question asks. Neither says what a client receives, and on the
 * corpus that is 102 findings and 139 questions — so a report is, in the common
 * case, mostly questions, and the document has to be built for that rather than
 * around it.
 *
 * ## It is a derivation, exactly as a statement is
 *
 * A `ReportDocument` is recomputed from the model, the findings and the
 * clarifications on every render and is never persisted, so it cannot drift
 * from the facts it reads (ZD-043's argument, one layer out). Nothing here is a
 * second copy of a finding: a section holds ids and references, and the words
 * come from the statements the packs already produced.
 *
 * ## The unit is the object, not the finding
 *
 * The measurement that decides it: on the corpus **241 items — 102 findings and
 * 139 questions — land on 53 objects**, every object a question asks about is
 * already the subject of something, and three patterns produce **52 of the 102
 * findings**, all firing on the same flows. A finding-ordered document prints
 * that repetition nineteen times and then has to decide where to put 139
 * questions relative to 52 assertions, which is a choice between reading as a
 * failure notice and reading as a burial. An object-ordered one does not have
 * that choice to make: each object says what is established and what is not, in
 * one place, and the ratio is whatever it is for that object.
 */

/**
 * The sections, in render order. Fixed, and every one is present every time.
 *
 * **A missing section and an empty section are different claims**, which is
 * `SLOT_KINDS`' rule and it matters more here: a report that silently lacks a
 * risk register lets a client supply their own ordering, and one that silently
 * lacks recommendations reads as *there is nothing to do*.
 */
export const REPORT_SECTIONS = [
  /** What we were given, what we could read, and what came out. The denominator. */
  'coverage',
  /** One entry per canonical object that carries a finding or a question. */
  'subjects',
  /** The consolidated question register — one entry per fact, not per record. */
  'open_questions',
  /** The extraction unknowns. A second register, for the reason `input_gaps` states. */
  'input_gaps',
  /** Where the documents disagree with each other. */
  'disagreements',
  /** What the catalogue directs, carried verbatim. */
  'directions',
  /** Severity and ranking. */
  'risk',
  /** What a reviewer decided, and what this report therefore does not contain. */
  'review',
  /** What to do, with acceptance criteria. */
  'recommendations',
  /** What this assessment is and is not. */
  'limits',
] as const;
export const ReportSectionKindSchema = z.enum(REPORT_SECTIONS);
export type ReportSectionKind = z.infer<typeof ReportSectionKindSchema>;

/**
 * Three states, and the third is the one that carries information.
 *
 * `empty` is a **declaration**: the producer ran and there is nothing — the
 * established-empty ZD-027 rule 2 permits, and the honest state of the
 * assumptions register on all four corpus systems. `unavailable` is *the
 * producer does not exist yet*, which is a different claim entirely and the one
 * a client must not read as `empty`. Collapsing them would tell a client that
 * nothing needs doing when what is true is that nobody has worked out what
 * needs doing (ZD-036 §2, one layer out from the collection rule).
 */
export const REPORT_SECTION_STATES = ['populated', 'empty', 'unavailable'] as const;
export const ReportSectionStateSchema = z.enum(REPORT_SECTION_STATES);
export type ReportSectionState = z.infer<typeof ReportSectionStateSchema>;

/** One artefact, and how much of it the pipeline could read (ZD-025). */
export const CoverageEntrySchema = z.object({
  artefact: NonEmptyStringSchema,
  units: z.number().int().nonnegative(),
  unreadable_pages: z.array(z.number().int().positive()),
});

/**
 * One canonical object, with everything the assessment has to say about it.
 *
 * The counts are derived from the id lists beside them rather than stored: a
 * count that can disagree with the list it counts is ZD-017's third fact.
 */
export const SubjectEntrySchema = z.object({
  object_id: z.string(),
  /** Resolved through the model, never an id — ZD-043 §8's first defect. */
  name: NonEmptyStringSchema,
  kind: NonEmptyStringSchema,
  /** Findings asserted about this object. */
  asserted_finding_ids: z.array(z.string()),
  /** Findings raised conditionally, waiting on an answer. */
  conditional_finding_ids: z.array(z.string()),
  /** `(object, attribute)` keys in `open_questions` that name this object. */
  question_keys: z.array(NonEmptyStringSchema),
  /**
   * Sentences somebody wrote about a finding under this object, printed
   * beneath that finding's line (docs/DECISIONS.md ZD-125 §2).
   *
   * The document renders findings by title and standing and never their
   * rendered description or rationale — 24 distinct descriptions across 102
   * corpus findings, generic to the pattern. A sentence a reviewer or a model
   * wrote is neither generic nor repeated: it is the one sentence in the
   * pipeline written for this client's finding, and it is the reason a
   * `provenanceLimit` computed over the statements' origins is a claim about
   * *this document* rather than about findings it does not reproduce. Empty on
   * every corpus system, since nothing there is written.
   */
  written: z.array(
    z.object({
      finding_id: z.string(),
      slot: SlotKindSchema,
      /** Who wrote it: `authored` is a model, `approver_edited` the reviewer. `rendered` writes nothing. */
      origin: ProseOriginSchema.exclude(['rendered']),
      text: NonEmptyStringSchema,
    }),
  ),
});
export type SubjectEntry = z.infer<typeof SubjectEntrySchema>;
export type WrittenSentence = SubjectEntry['written'][number];

/**
 * What one consolidated question bears on.
 *
 * **Per bearing rather than per question, and the split is measured rather than
 * assumed** (ZD-057 §2). Across the corpus's 70 facts, `standing` differs
 * between the records for one fact on **40**, whether answering decides the
 * pattern on **38**, and `blocks_navigation` on **11** — so a consolidated
 * question may not say *"no finding has been raised on this"*, because for 40
 * of them both answers are true of different threats.
 */
export const QuestionBearingSchema = z.object({
  threat_pattern_id: ThreatPatternIdSchema,
  /** The pattern's authored title, carried verbatim. */
  label: NonEmptyStringSchema,
  /** Whether a conditional finding already stands on this pattern and subject. */
  standing: z.enum(['conditional_finding', 'nothing_raised']),
  /** Whether answering settles this pattern, or only advances it. */
  decides: z.boolean(),
  subject_id: z.string(),
});
export type QuestionBearing = z.infer<typeof QuestionBearingSchema>;

/**
 * One fact a client is being asked for, however many records ask it.
 *
 * Keyed `(object, attribute)` with the bearing list attached, which is ZD-044
 * §5's own design confirmed against the data. `answer` and `establishable_from`
 * are **hoisted** because they are functions of the object's type and the
 * attribute and provably do not vary — 0 of 70 on the corpus — and `consolidate`
 * refuses to hoist either if it ever finds two records disagreeing, rather than
 * silently taking the first.
 */
export const ConsolidatedQuestionSchema = z.object({
  /** `${object_id}::${attribute}` — the consolidation key, stable and renderable. */
  key: NonEmptyStringSchema,
  object_id: z.string(),
  name: NonEmptyStringSchema,
  attribute: NonEmptyStringSchema,
  answer: AnswerShapeSchema,
  establishable_from: z.enum(['document', 'derivation']),
  bearings: z.array(QuestionBearingSchema).min(1),
  /** How many emitted records this stands for. The pair ZD-044 §5 pins together. */
  record_count: z.number().int().positive(),
});
export type ConsolidatedQuestion = z.infer<typeof ConsolidatedQuestionSchema>;

/**
 * One place the documents disagree, joined across two record types.
 *
 * **They are one disagreement and the model splits them**, which the corpus
 * demonstrates on a single flow: Tarnmouth's Manifest Exchange carries a typed
 * `Conflict` about `protocol` — *"SFTP over the private interconnect"* against
 * *"HTTPS (TLS 1.2)"*, both cited, grouped under a `cgroup_*` — **and** an
 * `Unknown` about `data`, free text, priority `low`, **with no citation at
 * all**, outside the group. Same flow, same two documents, same kind of
 * disagreement, two shapes and two registers.
 *
 * The cause is in `mergeAttribute` and is deliberate: a `text`-shaped attribute
 * that diverges without subsumption becomes a question, because a
 * `CompetingAssertion` requires a citation and ZD-015 forbids half a
 * contradiction.
 *
 * **This section therefore carries conflicts only, and the field that would
 * have carried the other half was designed, found unpopulatable, and removed.**
 * A `divergence_unknown_ids` list is exactly ZD-019's question with no honest
 * answer: nothing distinguishes a divergence `Unknown` from any other, because
 * `Unknown` has **no `kind`** — the producer knows precisely why it minted each
 * one (a dangling reference, a text divergence, an unreadable page, an absent
 * field, a refused merge) and the schema throws it away. The only discriminators
 * available are matching on the English the producer happened to write, which is
 * building on the defect, or parsing the `unknown_id`, which is a digest and
 * which ZD-004 forbids in any case.
 *
 * So the report cannot join them, a permanently-empty list would read as *there
 * are no divergences*, and the gap is recorded rather than papered over. It is
 * the ninth ZD-021-family instance and the second found before anything crossed
 * it (ZD-057 §5).
 */
export const DisagreementSchema = z.object({
  object_id: z.string(),
  name: NonEmptyStringSchema,
  /** Typed conflicts: attribute, the competing values, and both citations. */
  conflict_ids: z.array(z.string()).min(1),
});
export type Disagreement = z.infer<typeof DisagreementSchema>;

/**
 * A control the report directs, with the catalogue's lines carried by identity.
 *
 * `lines` is `controlOutputLines` output and nothing else. The efficacy caveat
 * and the legal-obligation framing reach a client through this field and no
 * other, and `assertVerbatim` compares them byte-for-byte with the catalogue's
 * own sentences before a document is returned.
 *
 * `implementation` is `controlImplementationLines` output and nothing else, and
 * it is a **separate field rather than three more `lines`** for two reasons.
 * The renderer has to label which tier is which, and a positional read off a
 * flat array is how ZD-057 §3's first defect happened. And the two travel under
 * different assertions: `lines` is identical to the catalogue, while
 * `implementation` is the catalogue under one named substitution — the
 * cross-reference resolution — so collapsing them would put two different
 * claims behind one comparison (ZD-060 §3).
 */
export const DirectionSchema = z.object({
  qrs_control_id: QrsControlIdSchema,
  lines: z.array(NonEmptyStringSchema).min(1),
  implementation: ControlImplementationSchema,
  /** The findings this control was cited by. */
  finding_ids: z.array(z.string()).min(1),
});
export type Direction = z.infer<typeof DirectionSchema>;

/**
 * One finding where the reviewer's view of the band differs from the derived
 * one — **carried beside it, never replacing it** (docs/DECISIONS.md ZD-061 §5).
 *
 * That entry says a dissent is *"recorded beside the band"*. Until this section
 * existed there was no beside: the report printed `derived` and the dissent
 * reached nobody outside the review log. A reader who quotes a band is exactly
 * the reader who needs to know a professional disagreed with it.
 *
 * **Both bands, and no reason.** The two bands are enum members and are
 * therefore client-safe by construction; `SeverityDissent.reason` is reviewer
 * free text with **zero instances anywhere in this repository**, so sds-05j's
 * audience check — which is per field and mechanical — cannot be run on it, and
 * the only client-language guard available fires inside `assembleReport`, one
 * transition after a person signed the text (ZD-080 §3). The section says the
 * reasons are recorded and not reproduced, so the omission is a declaration.
 */
export const BandDissentSchema = z.object({
  finding_id: z.string(),
  /** What this report prints for the finding. */
  derived: SeveritySchema,
  /** What the reviewer would have given it. */
  reviewer: SeveritySchema,
});
export type BandDissent = z.infer<typeof BandDissentSchema>;

const SectionBase = {
  state: ReportSectionStateSchema,
  /**
   * Why a section is `unavailable` or `empty`. Required for both, and refused
   * for `populated`.
   *
   * **This is the field that keeps the honest failure honest.** A risk section
   * that is absent is indistinguishable from one that is empty; one that says
   * *"no scoring method has run"* is a different document. ZD-035 §2's rule
   * that an omission and a decision must not have the same shape, applied where
   * the omission is ours rather than a framework's.
   */
  reason: NonEmptyStringSchema.nullable(),
  origin: ProseOriginSchema,
};

export const ReportSectionSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('coverage'),
    ...SectionBase,
    artefacts: z.array(CoverageEntrySchema),
    object_count: z.number().int().nonnegative(),
  }),
  z.object({ kind: z.literal('subjects'), ...SectionBase, entries: z.array(SubjectEntrySchema) }),
  z.object({
    kind: z.literal('open_questions'),
    ...SectionBase,
    questions: z.array(ConsolidatedQuestionSchema),
    /**
     * The record count this view stands for, kept beside the question count
     * because **either alone misleads** (ZD-044 §5). A consolidation must move
     * the first and must not move the second, and only a document carrying both
     * can be checked for that.
     */
    record_count: z.number().int().nonnegative(),
  }),
  z.object({
    kind: z.literal('input_gaps'),
    ...SectionBase,
    unknown_ids: z.array(z.string()),
  }),
  z.object({
    kind: z.literal('disagreements'),
    ...SectionBase,
    entries: z.array(DisagreementSchema),
  }),
  z.object({ kind: z.literal('directions'), ...SectionBase, controls: z.array(DirectionSchema) }),
  z.object({
    kind: z.literal('risk'),
    ...SectionBase,
    /**
     * The band distribution, strongest first — **not an ordered list of every
     * finding** (docs/DECISIONS.md ZD-058 §4).
     *
     * A severity-ordered register would be a second organisation of the same
     * items, and the measurement that chose the object unit says what it would
     * look like: three patterns produce 52 of the corpus's 102 findings, and a
     * band is constant within a pattern, so the register would open with
     * nineteen consecutive identical `high` rows about one threat on different
     * flows. That is exactly the repetition ZD-057 §1 chose the object unit to
     * avoid, re-imposed one section later.
     *
     * So the band travels with the finding inside its subject entry, and this
     * section is a denominator — like `coverage` — plus the sentence saying
     * what the ordering is. The flat ranked list is the `risk_register` report
     * type (DESIGN_SPEC §14.1), a different artefact for a different reader.
     */
    bands: z
      .array(
        z.object({
          severity: SeveritySchema,
          finding_ids: z.array(z.string()).min(1),
        }),
      )
      .max(SEVERITIES.length),
    /** Findings whose `scoring_method` is still `unscored`. */
    unscored_finding_ids: z.array(z.string()),
    /**
     * Findings banded without every input the method wanted, and which inputs
     * were missing — the honest half of a score (ZD-058 §6).
     *
     * A finding banded from `primary_loss_magnitude` alone and one banded from
     * four inputs carry the same `scoring_method` and are otherwise
     * indistinguishable here, which is how a thin score becomes a confident
     * one. The rule is `PatternMatchSchema`'s, two stages along: an outcome
     * that could not decide everything must name what it could not decide.
     */
    ranked_on_impact_only: z.array(z.string()),
  }),
  /**
   * What a reviewer decided, and — the part nothing else in the document can
   * say — **what this report therefore does not contain**.
   *
   * ## Why the section exists at all
   *
   * A report is built from `client_ready` findings only (DESIGN_SPEC §14.2), so
   * every finding a reviewer rejected is **silently absent** from every other
   * section. ZD-035 §2's rule is that an absence has to be a declaration and a
   * bare one is an error; this is the declaration.
   *
   * The argument is `EvidenceItemSchema`'s, carried across explicitly rather
   * than inherited (CLAUDE.md). That refinement refuses a pack carrying
   * `approved_findings` without `review_log` and `reviewer_identity_and_date`,
   * because a pack presenting the conclusion of a professional judgement with
   * none of the working is what the artefact exists not to be. **The mechanism
   * does not transfer** — a report has no `contents` list to refine over, so
   * this is a section rather than a refinement. **The argument transfers with
   * more force, not less**: that rule is one-directional, exempting a pack
   * assembled before the gate, and a report has exactly one inbound edge, out
   * of `approved_for_reporting`. A report always makes the claim.
   *
   * ## Counts, and only counts that nothing else in the document carries
   *
   * `reported_count` is deliberately absent: it is `reviewed_count` minus
   * `set_aside_count`, and a stored count that can disagree with the figures it
   * is derived from is ZD-017's third fact. `accepted_risk_count` is here
   * because `Treatment` reaches no other section — a risk register that does
   * not say which of its entries the client has already chosen to carry is a
   * different document from one that does.
   */
  z.object({
    kind: z.literal('review'),
    ...SectionBase,
    /** Findings a reviewer recorded a standing decision on. */
    reviewed_count: z.number().int().nonnegative(),
    /**
     * Findings the reviewer judged untrue of this system. Retained, never
     * deleted, and absent from every other section of this document.
     */
    set_aside_count: z.number().int().nonnegative(),
    /** Accepted findings the reviewer chose not to remediate. */
    accepted_risk_count: z.number().int().nonnegative(),
    /**
     * How many of the standing decisions were recorded as members of a group,
     * and how many groups (docs/DECISIONS.md ZD-127 §4). Derived from the
     * standing set, so a batched decision the reviewer later superseded stops
     * counting. The reason a group gave stays where every reason stays:
     * recorded against the assessment and not reproduced.
     *
     * This is what lets the section say *recorded a decision on each of 36
     * findings, 35 of them in one group decision* rather than *worked
     * through 36 findings* — the first is what the record can back. Over a
     * record whose decisions predate `batch_id`, both are zero and the
     * sentence carries no group clause: the record declared none.
     */
    batched_decision_count: z.number().int().nonnegative(),
    batch_count: z.number().int().nonnegative(),
    /** Where the reviewer's view of a band differs from the derived one. */
    band_dissents: z.array(BandDissentSchema),
    /**
     * When the latest standing decision was recorded.
     *
     * The date and not the approver's id: a `user_id` is an opaque internal
     * identifier and would be ZD-043 §8's defect in the paragraph a client
     * reads most carefully. Who approved is in the audit trail, which is where
     * an auditor asks for it.
     */
    decided_through: TimestampSchema.nullable(),
  }),
  z.object({
    kind: z.literal('recommendations'),
    ...SectionBase,
    mapping_ids: z.array(z.string()),
  }),
  z.object({
    kind: z.literal('limits'),
    ...SectionBase,
    statements: z.array(NonEmptyStringSchema),
  }),
]);
export type ReportSection = z.infer<typeof ReportSectionSchema>;

/** Whether a section is carrying content, whatever it claims about itself. */
function sectionIsPopulated(section: ReportSection): boolean {
  switch (section.kind) {
    case 'coverage':
      return section.artefacts.length > 0;
    case 'subjects':
    case 'disagreements':
      return section.entries.length > 0;
    case 'open_questions':
      return section.questions.length > 0;
    case 'input_gaps':
      return section.unknown_ids.length > 0;
    case 'directions':
      return section.controls.length > 0;
    case 'risk':
      /*
       * **This branch used to be inverted, and the inversion is gone because
       * the section acquired content** (ZD-058 §4).
       *
       * Until `sds-05g` nothing scored anything, so the only thing this section
       * could carry was its own absence, and `populated` had to mean *the
       * unscored list is empty* — with a comment warning the next reader not to
       * "simplify" it into agreement with the others. It now carries bands, so
       * it agrees with them for a reason rather than by simplification, and
       * removing the warning is part of the change rather than a lapse.
       *
       * The three states stay three: `populated` is a scored assessment,
       * `unavailable` is scoring not having run, and `empty` is a scored
       * assessment with no findings in it — which is Brackenwell's shape one
       * pack revision away, and reads as *nothing to rank* rather than as
       * *nobody ranked anything*.
       */
      return section.bands.length > 0;
    case 'review':
      /*
       * The reviewer, not the outcome. A review that set every finding aside
       * still happened, and a section reporting that as `empty` would tell a
       * client nobody looked — which is the one thing this section exists to
       * stop the document saying by omission.
       */
      return section.reviewed_count > 0;
    case 'recommendations':
      return section.mapping_ids.length > 0;
    case 'limits':
      return section.statements.length > 0;
  }
}

export const ReportDocumentSchema = z
  .object({
    assessment_id: NonEmptyStringSchema,
    system_name: NonEmptyStringSchema,
    /**
     * `pack_id@version` for every pack that ran — the qualification that makes
     * the document's claim precise. A report does not say *"these are the
     * threats in your system"*; it says *"these are the threats these packs, at
     * these versions, express about the model built from these documents"*, and
     * this field is the only part of that sentence a reader can check.
     */
    pack_bindings: z.array(NonEmptyStringSchema).min(1),
    sections: z.array(ReportSectionSchema),
  })
  .superRefine((document, ctx) => {
    const seen = new Set<ReportSectionKind>();

    document.sections.forEach((section, i) => {
      if (seen.has(section.kind)) {
        ctx.addIssue({
          code: 'custom',
          message: `duplicate section ${section.kind}`,
          path: ['sections', i, 'kind'],
        });
      }
      seen.add(section.kind);

      const populated = sectionIsPopulated(section);

      if (section.state === 'populated' && !populated) {
        ctx.addIssue({
          code: 'custom',
          message: `section ${section.kind} is populated and carries nothing`,
          path: ['sections', i],
        });
      }
      if (section.state === 'empty' && populated) {
        ctx.addIssue({
          code: 'custom',
          message:
            `section ${section.kind} is empty and carries content — an absence must not be ` +
            `able to dress itself as a claim`,
          path: ['sections', i],
        });
      }
      /*
       * `unavailable` is about the producer, not about the data, so it is the
       * one state where content is a contradiction rather than a mismatch: a
       * section listing entries while saying nobody computed them is claiming
       * two incompatible things at once.
       */
      if (section.state === 'unavailable' && populated) {
        ctx.addIssue({
          code: 'custom',
          message: `section ${section.kind} is unavailable and carries content`,
          path: ['sections', i],
        });
      }

      if (section.state === 'populated' && section.reason !== null) {
        ctx.addIssue({
          code: 'custom',
          message: `section ${section.kind} is populated and does not need a reason`,
          path: ['sections', i, 'reason'],
        });
      }
      if (section.state !== 'populated' && section.reason === null) {
        ctx.addIssue({
          code: 'custom',
          message:
            `section ${section.kind} is ${section.state} and gives no reason — a client ` +
            `cannot tell "there is nothing" from "nobody worked it out"`,
          path: ['sections', i, 'reason'],
        });
      }
    });

    /*
     * Every section, every time — the rule `FindingStatementSchema` applies to
     * slots, and load-bearing for the same reason: a document that omits its
     * risk section and one whose risk section says why it is unavailable are
     * different documents, and only the second is honest.
     */
    for (const kind of REPORT_SECTIONS) {
      if (seen.has(kind)) continue;
      ctx.addIssue({
        code: 'custom',
        message: `document omits section ${kind}`,
        path: ['sections'],
      });
    }
  });
export type ReportDocument = z.infer<typeof ReportDocumentSchema>;

/** The section of a given kind. Every document carries all of them. */
export function sectionOf<K extends ReportSectionKind>(
  document: ReportDocument,
  kind: K,
): Extract<ReportSection, { kind: K }> {
  const section = document.sections.find((candidate) => candidate.kind === kind);
  if (!section) throw new Error(`document has no ${kind} section`);
  return section as Extract<ReportSection, { kind: K }>;
}
